/**
 * This file copies over the logic needed to support the
 * legacy signature modal. See hellosign/components/app/header.
 *
 * This component acts as interface between the new layout
 * and the old signature modal.
 */

import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { getCSRFToken } from 'hellospa/hs-fetch';
import SignatureModal from 'signer/components/common/signature-modal/edit-signature-modal';
import CONSTANTS from 'common/signature-modal/constants';

declare global {
  interface Window { app?: any; }
}

const messages = defineMessages({
  select: {
    id: '',
    description: 'button text in signer modal, when clicked user can select from list of saved signatures/initials',
    defaultMessage: 'Select',
  },
  save: {
    id: '',
    description: 'button text in signer modal, when clicked user can save the signature/initial',
    defaultMessage: 'Save',
  },
});

type SignatureModalProps = {
  app?: any,
  signatureModalProps?: {
    signatureType?: string,
    settings?: Object,
    hasPaginatedSignatures?: boolean,
    csrfToken?: string,
  },
  onRequestClose: Function,
};

function SignatureModalInterface({
  app,
  signatureModalProps,
  onRequestClose,
}: SignatureModalProps) {
  const intl = useIntl();
  const [tab, setTab] = React.useState(CONSTANTS.TAB_SAVED);

  React.useEffect(() => {
    // CSRF Token is used in the in-app signature modal.
    if (app && signatureModalProps) {
      app.csrfToken = signatureModalProps?.csrfToken;
    } else {
      app.csrfToken = getCSRFToken();
    }
  }, [app, signatureModalProps]);

  const onSignatureModalTabSelected = React.useCallback((tabId) => {
    setTab(tabId);
  }, []);

  const updatePrimarySignature = React.useCallback((signature) => {
    window.primarySignatureGuid = signature ? signature.guid : undefined;

    if (tab === CONSTANTS.TAB_SAVED) {

      // Mark the current signature as primary and close the modal
      signature.markAsPrimary().then(() => {
        onRequestClose();
      }).catch(() => {
        // Fail silently
        console.error('Could not mark signature as primary');
      });
    } else {

      // Show the saved signature tab after a new one has been created
      setTab(CONSTANTS.TAB_SAVED);
    }
  }, [tab, onRequestClose]);

  const modalProps = React.useMemo(() => {
    if (signatureModalProps?.settings) {
      app.isMobile = false;
      app.user.settings = Object.assign(
        app.user.settings || {},
        signatureModalProps.settings,
      );
    }

    return {
      intl,
      app: app || window.app,
      buttonText: tab === CONSTANTS.TAB_SAVED
        ? intl.formatMessage(messages.select) : intl.formatMessage(messages.save),
      type: signatureModalProps?.signatureType,
      onComplete: updatePrimarySignature,
      onTabSelect: onSignatureModalTabSelected,
      onClose: onRequestClose,
      initialTab: tab,
      hasPaginatedSignatures: signatureModalProps?.hasPaginatedSignatures,
    };
  }, [
    app,
    signatureModalProps,
    tab,
    intl,
    onRequestClose,
    onSignatureModalTabSelected,
    updatePrimarySignature,
  ]);

  return (
    <SignatureModal {...modalProps} />
  );
}

export default SignatureModalInterface;
